
import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit2, Trash2, AlertTriangle, CheckCircle, 
  Save, Calendar as CalendarIcon, Clock, X, Loader2
} from 'lucide-react';
import { useSchool } from '../../../context/SchoolContext';
import { academicApi } from '../services/academicApi';
import { timetableApi } from '../services/timetableApi';
import { staffApi } from '../../staff/services/staffApi';
import { facilityApi } from '../../facilities/services/facilityApi';

const TimetablePage = () => {
  const { schoolId, activeAcademicYear, activeAcademicYearId } = useSchool();
  const selectedYear = activeAcademicYearId || '';

  // Master Data
  const [classLevels, setClassLevels] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [facilities, setFacilities] = useState([]);
  
  // Selection State
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [effectiveTo, setEffectiveTo] = useState('');
  
  // Timetable Data
  const [currentTimetable, setCurrentTimetable] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [slots, setSlots] = useState([]); // Array of slot objects
  
  // UI State
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  
  // Modals
  const [isPeriodModalOpen, setIsPeriodModalOpen] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState(null);
  
  const [isSlotModalOpen, setIsSlotModalOpen] = useState(false);
  const [selectedSlotCell, setSelectedSlotCell] = useState(null); // { period_id, day_of_week }

  // Load Master Data
  useEffect(() => {
    const loadMasterData = async () => {
      try {
        if (!schoolId || !selectedYear) {
          setClassLevels([]);
          setSections([]);
          setSubjects([]);
          return;
        }
        const [classes, secs, subs, staffRes, facRes] = await Promise.all([
          academicApi.getClassLevels(schoolId, { academicYearId: selectedYear, status: 'ACTIVE' }),
          academicApi.getSections(schoolId, { academicYearId: selectedYear, status: 'ACTIVE' }),
          academicApi.getSubjects(schoolId, { academicYearId: selectedYear, status: 'ACTIVE' }),
          staffApi.getStaff(schoolId, { status: 'ACTIVE', page_size: 500 }),
          facilityApi.getFacilities(schoolId)
        ]);
        setClassLevels(classes || []);
        setSections(secs || []);
        setSubjects(subs || []);
        setStaffList(staffRes || []);
        setFacilities(facRes || []);
      } catch (err) {
        console.error("Failed to load master data", err);
      }
    };
    loadMasterData();
  }, [schoolId, selectedYear]);

  // Load Periods
  const loadPeriods = async () => {
    try {
      const data = await timetableApi.getPeriods(schoolId);
      setPeriods(data || []);
    } catch (err) {
      console.error("Failed to load periods", err);
    }
  };

  useEffect(() => {
    loadPeriods();
  }, [schoolId]);

  // Handle Timetable Apply
  const handleApply = async () => {
    if (!selectedYear || !selectedClass || !selectedSection || !effectiveFrom || !effectiveTo) {
      alert("Please fill all timetable criteria (Year, Class, Section, Dates).");
      return;
    }
    
    setLoading(true);
    setConflicts([]);
    try {
      // Create new timetable (or backend could return existing based on criteria)
      const data = await timetableApi.createTimetable(schoolId, {
        academic_year_id: selectedYear,
        class_level_id: selectedClass,
        section_id: selectedSection,
        effective_from: effectiveFrom,
        effective_to: effectiveTo,
        remarks: "Generated Timetable"
      });
      // The API response is expected to have the timetable ID.
      // Wait, user gave GET /timetable/ response snippet but CREATE might just return the created object.
      // Assuming data returns { id: '...' } or we just fetch the list to find it.
      
      // For now, let's just assign what comes back or fetch the list.
      const list = await timetableApi.getTimetables(schoolId);
      if (list && list.length > 0) {
        // Find the one we just made, or just take the first for demo
        setCurrentTimetable(list[0]);
      } else if (data && data.id) {
        setCurrentTimetable(data);
      } else {
         // Fallback if structure is weird
         setCurrentTimetable({ id: 'dummy-id', ...data });
      }
      
      // Clear slots on new apply
      setSlots([]);
    } catch (err) {
      console.error(err);
      alert("Failed to initialize timetable.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedClass('');
    setSelectedSection('');
    setEffectiveFrom('');
    setEffectiveTo('');
    setCurrentTimetable(null);
    setSlots([]);
    setConflicts([]);
  };

  // Bulk Save Slots
  const handleSaveTimetable = async () => {
    if (!currentTimetable?.id) {
      alert("Please apply filters to initialize a timetable first.");
      return;
    }
    setSaving(true);
    setConflicts([]);
    try {
      await timetableApi.bulkCreateSlots(schoolId, currentTimetable.id, { slots });
      alert("Timetable published successfully!");
    } catch (err) {
      console.error("Bulk create failed", err);
      // Parse 400 errors for conflicts
      if (err.response && err.response.status === 400) {
        const errorData = err.response.data;
        // Assuming the backend returns a list of conflicts in the response
        if (errorData.errors && Array.isArray(errorData.errors)) {
          setConflicts(errorData.errors);
        } else if (errorData.message) {
          setConflicts([errorData.message]);
        } else {
          setConflicts(["Timetable contains conflicts. Please review."]);
        }
      } else {
        alert("An error occurred while saving the timetable.");
      }
    } finally {
      setSaving(false);
    }
  };

  const DAYS = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];

  return (
    <div className="flex flex-col h-full bg-gray-50 p-6 space-y-6 overflow-y-auto">
      
      {/* Header */}
      <div className="flex justify-between items-center bg-white p-4 rounded-xl shadow-sm border border-gray-200">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Timetable Management</h1>
          <p className="text-gray-500 text-sm">Build and publish class timetables</p>
        </div>
        <div className="flex gap-3">
          <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm transition-colors">
            Set Active
          </button>
        </div>
      </div>

      {/* Create Timetable */}
      <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-gray-900">Create Timetable</h3>
          {currentTimetable && (
            <span className="text-xs font-medium bg-green-100 text-green-700 px-2 py-1 rounded">
              Active Draft: {currentTimetable.id.substring(0, 8)}...
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Working Academic Year</label>
            <div className="w-full rounded-lg border border-blue-200 bg-blue-50 p-2 text-sm font-semibold text-blue-800">
              {activeAcademicYear?.year_name || 'No academic year selected'}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Class Level</label>
            <select 
              value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select Class</option>
              {classLevels.map(c => (
                <option key={c.id} value={c.id}>{c.class_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Section</label>
            <select 
              value={selectedSection} onChange={(e) => setSelectedSection(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select Section</option>
              {sections.map(s => (
                <option key={s.id} value={s.id}>{s.section_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Effective Dates</label>
            <div className="flex items-center gap-2">
              <input 
                type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500"
              />
              <span className="text-gray-400">-</span>
              <input 
                type="date" value={effectiveTo} onChange={(e) => setEffectiveTo(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={handleApply}
              disabled={loading}
              className="flex-1 bg-blue-600 text-white p-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-70 flex justify-center"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Timetable"}
            </button>
            <button 
              onClick={handleReset}
              className="flex-1 border border-gray-300 text-gray-700 p-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Layout */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6 items-start">
        
        {/* Left Column (Periods + Grid) */}
        <div className="xl:col-span-3 space-y-6">
          
          {/* Period Setup */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
              <div>
                <h3 className="font-semibold text-gray-900">Period Setup</h3>
                <p className="text-xs text-gray-500">Define daily periods and breaks</p>
              </div>
              <button 
                onClick={() => {
                  setEditingPeriod(null);
                  setIsPeriodModalOpen(true);
                }}
                className="flex items-center gap-2 bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" /> Add Period
              </button>
            </div>
            
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-white border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wider">
                  <th className="p-3 font-medium">#</th>
                  <th className="p-3 font-medium">Period Name</th>
                  <th className="p-3 font-medium">Start</th>
                  <th className="p-3 font-medium">End</th>
                  <th className="p-3 font-medium text-center">Type</th>
                  <th className="p-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="text-sm divide-y divide-gray-100">
                {periods.sort((a,b) => a.sequence - b.sequence).map((p, idx) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="p-3 text-gray-500">{idx + 1}</td>
                    <td className="p-3 font-medium text-gray-900">{p.name}</td>
                    <td className="p-3 text-gray-600">{p.start_time?.substring(0, 5)}</td>
                    <td className="p-3 text-gray-600">{p.end_time?.substring(0, 5)}</td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold tracking-wide ${
                        p.is_break ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {p.is_break ? 'BREAK' : 'PERIOD'}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <button 
                        onClick={() => {
                          setEditingPeriod(p);
                          setIsPeriodModalOpen(true);
                        }}
                        className="p-1.5 text-gray-400 hover:text-blue-600 rounded transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {periods.length === 0 && (
                  <tr>
                    <td colSpan="6" className="p-6 text-center text-gray-500 text-sm">
                      No periods configured. Click "Add Period" to start.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Timetable Grid Builder */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="font-semibold text-gray-900">Timetable Grid Builder</h3>
              <p className="text-xs text-gray-500">Click any cell to add or edit a slot</p>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[800px]">
                <thead>
                  <tr className="bg-white border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wider">
                    <th className="p-4 font-medium w-32 border-r border-gray-200">Period</th>
                    {DAYS.map(day => (
                      <th key={day} className="p-4 font-medium text-center">{day}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="text-sm divide-y divide-gray-100">
                  {periods.sort((a,b) => a.sequence - b.sequence).map(period => (
                    <tr key={period.id}>
                      {/* Row Header */}
                      <td className="p-4 border-r border-gray-200 align-top bg-gray-50">
                        <div className="font-medium text-gray-900">{period.name}</div>
                        <div className="text-xs text-gray-500">{period.start_time?.substring(0, 5)} - {period.end_time?.substring(0, 5)}</div>
                      </td>
                      
                      {/* Cells */}
                      {DAYS.map(day => {
                        const cellSlot = slots.find(s => s.period_id === period.id && s.day_of_week === day);
                        const subject = cellSlot ? subjects.find(sub => sub.id === cellSlot.subject_id) : null;
                        const teacher = cellSlot ? staffList.find(st => st.id === cellSlot.staff_id) : null;
                        const room = cellSlot ? facilities.find(f => f.id === cellSlot.room_id) : null;

                        return (
                          <td key={`${period.id}-${day}`} className="p-2 border-r border-gray-100 last:border-r-0 relative group">
                            {period.is_break ? (
                              <div className="flex items-center justify-center h-20 text-orange-500 font-semibold text-xs tracking-wider uppercase opacity-50">
                                Break
                              </div>
                            ) : cellSlot ? (
                              <div 
                                onClick={() => {
                                  setSelectedSlotCell({ period, day, existing: cellSlot });
                                  setIsSlotModalOpen(true);
                                }}
                                className="bg-blue-50 border border-blue-100 rounded-lg p-2 h-24 flex flex-col justify-center cursor-pointer hover:border-blue-300 transition-colors"
                              >
                                <div className="font-medium text-blue-900 text-sm truncate">{subject?.subject_name}</div>
                                <div className="text-xs text-blue-700 truncate">{teacher?.full_name}</div>
                                <div className="text-xs text-blue-500 mt-1">{room?.name}</div>
                              </div>
                            ) : (
                              <div 
                                onClick={() => {
                                  setSelectedSlotCell({ period, day });
                                  setIsSlotModalOpen(true);
                                }}
                                className="border border-dashed border-gray-300 rounded-lg p-2 h-24 flex items-center justify-center text-gray-400 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-500 cursor-pointer transition-colors"
                              >
                                <Plus className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {periods.length === 0 && (
                    <tr>
                      <td colSpan="7" className="p-8 text-center text-gray-500">
                        Please define periods above to build the timetable grid.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Grid Bottom Save Button */}
            <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end">
              <button 
                onClick={handleSaveTimetable}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-bold transition-colors disabled:opacity-70 shadow-sm"
              >
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                Save Timetable Slots
              </button>
            </div>
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="space-y-6">
          
          {/* Conflict Detection */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
              <h3 className="font-semibold text-gray-900">Conflict Detection</h3>
              <button className="text-xs text-blue-600 font-medium hover:underline">Run Check</button>
            </div>
            <div className="p-4 space-y-3">
              {conflicts.length > 0 ? (
                conflicts.map((conflict, idx) => (
                  <div key={idx} className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-red-900">Conflict Detected</p>
                        <p className="text-xs text-red-700 mt-0.5">{typeof conflict === 'string' ? conflict : JSON.stringify(conflict)}</p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center text-center py-6 text-gray-500">
                  <CheckCircle className="w-8 h-8 text-green-500 mb-2 opacity-80" />
                  <p className="text-sm">No conflicts detected.</p>
                </div>
              )}
            </div>
          </div>

          {/* Teacher Availability (Mock) */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="font-semibold text-gray-900">Teacher Availability</h3>
            </div>
            <div className="p-4 space-y-3">
              {staffList.slice(0, 3).map(staff => (
                <div key={staff.id} className="flex justify-between items-center p-2 rounded-lg border border-gray-100 hover:bg-gray-50">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{staff.full_name}</p>
                    <p className="text-xs text-gray-500">{staff.designation}</p>
                  </div>
                  <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded">AVAILABLE</span>
                </div>
              ))}
            </div>
          </div>

          {/* Export */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden p-4 space-y-3">
            <h3 className="font-semibold text-gray-900 mb-2">Export & Print</h3>
            <button className="w-full bg-red-600 text-white font-medium text-sm p-2.5 rounded-lg hover:bg-red-700 transition-colors">
              Export PDF
            </button>
            <button className="w-full bg-white border border-gray-300 text-gray-700 font-medium text-sm p-2.5 rounded-lg hover:bg-gray-50 transition-colors">
              Print
            </button>
          </div>

        </div>
      </div>

      {/* MODALS */}
      {isPeriodModalOpen && (
        <PeriodModal 
          isOpen={isPeriodModalOpen}
          onClose={() => setIsPeriodModalOpen(false)}
          editingPeriod={editingPeriod}
          schoolId={schoolId}
          onSuccess={() => {
            setIsPeriodModalOpen(false);
            loadPeriods();
          }}
        />
      )}

      {isSlotModalOpen && selectedSlotCell && (
        <SlotModal
          isOpen={isSlotModalOpen}
          onClose={() => setIsSlotModalOpen(false)}
          cellData={selectedSlotCell}
          subjects={subjects}
          staffList={staffList}
          facilities={facilities}
          onSaveSlot={(slotData) => {
            // Merge into slots array
            const newSlots = [...slots];
            const existingIndex = newSlots.findIndex(s => 
              s.period_id === slotData.period_id && s.day_of_week === slotData.day_of_week
            );
            if (existingIndex > -1) {
              newSlots[existingIndex] = slotData;
            } else {
              newSlots.push(slotData);
            }
            setSlots(newSlots);
            setIsSlotModalOpen(false);
          }}
        />
      )}

    </div>
  );
};

// ----------------------------------------------------
// PERIOD MODAL
// ----------------------------------------------------
const PeriodModal = ({ isOpen, onClose, editingPeriod, schoolId, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    sequence: 1,
    start_time: '08:00',
    end_time: '08:45',
    is_break: false
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editingPeriod) {
      setFormData({
        name: editingPeriod.name || '',
        sequence: editingPeriod.sequence || 1,
        start_time: editingPeriod.start_time?.substring(0, 5) || '08:00',
        end_time: editingPeriod.end_time?.substring(0, 5) || '08:45',
        is_break: editingPeriod.is_break || false
      });
    }
  }, [editingPeriod]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        start_time: `${formData.start_time}:00.000Z`,
        end_time: `${formData.end_time}:00.000Z`
      };
      if (editingPeriod) {
        await timetableApi.updatePeriod(schoolId, editingPeriod.id, payload);
      } else {
        await timetableApi.createPeriod(schoolId, payload);
      }
      onSuccess();
    } catch (err) {
      console.error(err);
      alert("Failed to save period.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{editingPeriod ? 'Edit Period' : 'Add Period'}</h2>
            <p className="text-xs text-gray-500">Configure period name, time and type</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4 flex-1 overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Period Name *</label>
            <input 
              type="text" required
              value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})}
              placeholder="e.g. Period 1, Lunch Break"
              className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sequence Order *</label>
            <input 
              type="number" required min="1"
              value={formData.sequence} onChange={e => setFormData({...formData, sequence: parseInt(e.target.value)})}
              className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-gray-50 cursor-pointer">
            <input 
              type="checkbox" 
              checked={formData.is_break} 
              onChange={e => setFormData({...formData, is_break: e.target.checked})}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">This is a break period (Recess / Lunch)</span>
          </label>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Time *</label>
              <input 
                type="time" required
                value={formData.start_time} onChange={e => setFormData({...formData, start_time: e.target.value})}
                className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Time *</label>
              <input 
                type="time" required
                value={formData.end_time} onChange={e => setFormData({...formData, end_time: e.target.value})}
                className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </form>
        
        <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
          <button 
            type="button" onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button 
            onClick={handleSubmit} disabled={saving}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-70"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {editingPeriod ? 'Update Period' : 'Add Period'}
          </button>
        </div>
      </div>
    </div>
  );
};


// ----------------------------------------------------
// SLOT MODAL
// ----------------------------------------------------
const SlotModal = ({ isOpen, onClose, cellData, subjects, staffList, facilities, onSaveSlot }) => {
  const [formData, setFormData] = useState({
    day_of_week: cellData.day,
    period_id: cellData.period.id,
    slot_type: 'THEORY',
    subject_id: '',
    staff_id: '',
    room_id: '',
    notes: ''
  });

  useEffect(() => {
    if (cellData.existing) {
      setFormData({
        ...formData,
        slot_type: cellData.existing.slot_type || 'THEORY',
        subject_id: cellData.existing.subject_id || '',
        staff_id: cellData.existing.staff_id || '',
        room_id: cellData.existing.room_id || '',
        notes: cellData.existing.notes || ''
      });
    }
  }, [cellData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.subject_id || !formData.staff_id || !formData.room_id) {
      alert("Subject, Teacher, and Room are required.");
      return;
    }
    onSaveSlot(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end z-50">
      <div className="bg-white h-full w-full max-w-md shadow-2xl flex flex-col animate-slide-in-right">
        
        <div className="p-6 border-b border-gray-200 flex justify-between items-start bg-gray-50">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Add Slot</h2>
            <p className="text-sm text-gray-500 mt-1 capitalize">{cellData.day} • {cellData.period.name}</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-200">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-5 flex-1 overflow-y-auto">
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Day of Week</label>
              <input 
                type="text" disabled value={cellData.day}
                className="w-full p-2.5 bg-gray-100 border border-gray-300 rounded-lg text-sm text-gray-600 capitalize"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Slot Type</label>
              <select 
                value={formData.slot_type} onChange={e => setFormData({...formData, slot_type: e.target.value})}
                className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="THEORY">THEORY</option>
                <option value="PRACTICAL">PRACTICAL</option>
                <option value="ACTIVITY">ACTIVITY</option>
                <option value="LIBRARY">LIBRARY</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
            <select 
              required value={formData.subject_id} onChange={e => setFormData({...formData, subject_id: e.target.value})}
              className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select subject...</option>
              {subjects.map(s => (
                <option key={s.id} value={s.id}>{s.subject_name} ({s.subject_code})</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Teacher *</label>
            <select 
              required value={formData.staff_id} onChange={e => setFormData({...formData, staff_id: e.target.value})}
              className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select teacher...</option>
              {staffList.map(s => (
                <option key={s.id} value={s.id}>{s.full_name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Room No *</label>
            <select 
              required value={formData.room_id} onChange={e => setFormData({...formData, room_id: e.target.value})}
              className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select room...</option>
              {facilities.map(f => (
                <option key={f.id} value={f.id}>{f.name} ({f.room_code})</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
            <textarea 
              value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})}
              placeholder="Any specific instructions..."
              className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500 h-24 resize-none"
            ></textarea>
          </div>

        </form>
        
        <div className="p-6 border-t border-gray-200 bg-white grid grid-cols-2 gap-4">
          <button 
            type="button" onClick={onClose}
            className="w-full py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button 
            onClick={handleSubmit}
            className="w-full py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex justify-center items-center gap-2"
          >
            <CheckCircle className="w-4 h-4" /> Save Slot
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimetablePage;
